// Permission utility functions for role-based access control
import axiosInstance from '../api/axiosInstance';
import { baseurl } from '../Baseurl';

// Cache for module mappings
let moduleMappingsCache = null;

// Get user permissions from localStorage
export const getUserPermissions = () => {
    try {
        const permissions = localStorage.getItem("userPermissions");
        if (permissions) {
            return JSON.parse(permissions);
        }
        return [];
    } catch (error) {
        console.error("Error parsing userPermissions:", error);
        return [];
    }
};

// Get user ID from localStorage
export const getUserId = () => {
    try {
        const userMedical = localStorage.getItem("userMedical");
        if (userMedical) {
            const userData = JSON.parse(userMedical);
            return userData?.id || userData?.data?.id || null;
        }
        return null;
    } catch (error) {
        console.error("Error parsing userMedical:", error);
        return null;
    }
};

// Check if current user is admin (by role field)
export const isAdmin = () => {
    try {
        const userMedical = localStorage.getItem("userMedical");
        if (userMedical) {
            const userData = JSON.parse(userMedical);
            const role = userData?.role || userData?.data?.role || "";
            return role.toLowerCase() === "admin";
        }
        return false;
    } catch {
        return false;
    }
};
export const fetchModuleMappings = async () => {
    if (moduleMappingsCache) {
        return moduleMappingsCache;
    }
    try {
        const response = await axiosInstance.get(`getAllModulesWithSubModules`);
        if (response.data.success && response.data.data) {
            moduleMappingsCache = response.data.data;
            return moduleMappingsCache;
        }
        return [];
    } catch (error) {
        console.error("Error fetching module mappings:", error);
        return [];
    }
};

// Mapping of menu paths to module names (from API)
export const menuToModuleMap = {
    "/Homepage": null, // Always accessible
    "/Admin/dashboard": "dashboard",
    "/Admin/patient": "patients",
    "/Admin/appointment2": "appointments",
    "/Admin/ManageLabs": "labs",
    "/Admin/Billing": "billing",
    "/Admin/Pharmacy": "pharmacy",
    "/Admin/Medicals": "medicals",
    "/Admin/Insurance": "insurance",
    "/Admin/Service": "services",
    "/Admin/ReportsInc": "reports",
    "/Admin/setting": "settings",
    "/Admin/BarcodeScanner": "pharmacy",
};

// Get sub_module_ids for a given module name
export const getSubModuleIdsForModule = async (moduleName) => {
    if (!moduleName) return [];

    const modules = await fetchModuleMappings();
    const module = modules.find(m => m.module_name === moduleName);

    if (module && module.sub_modules) {
        return module.sub_modules.map(sm => sm.id);
    }

    return [];
};

// Check if user has permission for a specific menu path
export const hasPermission = async (menuPath) => {
    // Admin has access to everything
    if (isAdmin()) return true;

    const moduleName = menuToModuleMap[menuPath];

    // If no module mapping exists (null), allow access (Home, Dashboard)
    if (moduleName === null) return true;

    // Get required sub_module_ids for this module
    const requiredPermissions = await getSubModuleIdsForModule(moduleName);

    if (requiredPermissions.length === 0) return true;

    const userPermissions = getUserPermissions();

    // Check if user has at least one of the required permissions
    return requiredPermissions.some(permId => userPermissions.includes(permId));
};

// Add disabled property to menu items based on permissions
export const addDisabledStateToMenu = async (menuItems) => {
    const menuWithState = await Promise.all(
        menuItems.map(async (item) => {
            const hasAccess = await hasPermission(item.path || item.url);
            return {
                ...item,
                disabled: !hasAccess
            };
        })
    );
    return menuWithState;
};


// Check if user has permission for a specific sub_module_id
export const hasSubModulePermission = (subModuleId) => {
    // Admin has access to everything
    if (isAdmin()) return true;

    // Return false if subModuleId is invalid
    if (!subModuleId || subModuleId === undefined || subModuleId === null) {
        return false;
    }

    const userPermissions = getUserPermissions();
    return userPermissions.includes(subModuleId);
};

// Fetch and update user permissions from API
export const updatePermissions = async () => {
    const userId = getUserId();
    if (!userId) return;

    try {
        const response = await axiosInstance.get(`getUserPermissions/${userId}`);
        if (response.data.success && response.data.data) {
            const permissions = response.data.data.map(item => item.sub_module_id);
            localStorage.setItem("userPermissions", JSON.stringify(permissions));
        }
    } catch (error) {
        console.error("Error updating permissions:", error);
    }
};
